"use client"

// ChartLightbox: an inline chart that expands to a full-screen modal on click,
// with a "Volver" (close) control in the top bar. Mirrors ImageLightbox but for
// client-rendered Recharts charts: instead of an <img src>, it takes the chart
// as children and renders it twice — small inline (the trigger) and large inside
// the modal. Because Recharts is responsive (ResponsiveContainer), the modal copy
// redraws at the larger size and stays crisp (SVG, no pixelation).
//
// Built on the Radix Dialog primitive so it gets focus trap, Escape-to-close,
// scroll lock, and click-outside-to-close for free.

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { ArrowLeft, Maximize2 } from "lucide-react"
import type { ReactNode } from "react"

interface ChartLightboxProps {
  /** Accessible title (the artifact/deliverable name). */
  title: string
  /** The inline chart to show as the clickable thumbnail. */
  children: ReactNode
  /**
   * The chart to render enlarged inside the modal. Usually the same chart as
   * `children` but sized for a large viewport. Kept separate so the caller can
   * apply a taller min-height / different margins for the expanded view.
   */
  expanded: ReactNode
}

/**
 * Clickable inline chart that opens a full-screen chart viewer.
 *
 * @param title - accessible title text.
 * @param children - the inline (small) chart used as the trigger.
 * @param expanded - the enlarged chart rendered inside the modal.
 */
export function ChartLightbox({ title, children, expanded }: ChartLightboxProps): ReactNode {
  return (
    <DialogPrimitive.Root>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={`Ampliar gráfica: ${title}`}
          className="group relative block w-full cursor-zoom-in rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {children}
          {/* Zoom affordance: a magnifier badge in the top-right corner that
              hints the chart is clickable. Subtle by default, emphasized on
              hover/focus of the chart. */}
          <span
            aria-hidden="true"
            className="absolute right-2 top-2 z-10 inline-flex items-center justify-center rounded-md border border-chart-card-border bg-card/80 p-1.5 text-muted-foreground opacity-60 shadow-sm backdrop-blur transition group-hover:opacity-100 group-hover:text-foreground group-focus-visible:opacity-100"
          >
            <Maximize2 className="size-4" />
          </span>
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95"
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>

          {/* Clicking the empty backdrop area closes the viewer. */}
          <DialogPrimitive.Close
            aria-label="Cerrar"
            className="absolute inset-0 cursor-zoom-out outline-none"
          />

          {/* Top toolbar: Volver (left). */}
          <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-2 p-4">
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full bg-card/90 px-3.5 py-2 text-sm font-medium text-foreground shadow-md backdrop-blur transition-colors hover:bg-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <ArrowLeft className="size-4" aria-hidden="true" />
                <span>Volver</span>
              </button>
            </DialogPrimitive.Close>
          </div>

          {/* The enlarged chart panel. Sits above the backdrop close so clicking
              it keeps the viewer open; only the surrounding empty space closes. */}
          <div className="relative z-10 flex max-h-[90vh] w-[92vw] max-w-5xl flex-col overflow-hidden rounded-chart-card border border-chart-card-border bg-chart-card shadow-2xl">
            <div className="flex items-center px-4 pt-3">
              <span className="min-w-0 flex-1 truncate text-base font-medium text-foreground">
                {title}
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto px-4 py-4">{expanded}</div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
